import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react'
import { View, Text, ScrollView, StyleSheet, Image, Platform, PanResponder } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { KeepContact, useUpdateKeepContact } from '@hooks'
import { defaultImage } from '@assets'
import { parseRrule, type TranslateFn, getRecurrenceLabels, buildRrule, SimpleRecurrenceType, showApiErrorToast } from '@utils'

interface BoardViewProps {
  keepContacts: KeepContact[]
  onEditReminder: (reminder: KeepContact) => void
  t: TranslateFn
}

type FrequencyColumn = {
  key: string
  label: string
  items: KeepContact[]
}

const getFrequencyKey = (kc: KeepContact): string => {
  if (!kc.rrule) return 'never'
  const { type } = parseRrule(kc.rrule, new Date(kc.keepDate))
  return type
}

const COLUMN_WIDTH = 260
// COLUMN_WIDTH + 2 * marginHorizontal(8)
const COLUMN_TOTAL = COLUMN_WIDTH + 16

interface DraggableCardProps {
  item: KeepContact
  styles: ReturnType<typeof getStyles>
  onPress: () => void
  onDragStart: (item: KeepContact, pageX: number, pageY: number, offsetX: number, offsetY: number) => void
  onDragMove: (pageX: number, pageY: number) => void
  onDragEnd: (pageX: number, pageY: number) => void
  isDragging: boolean
}

const DraggableCard: React.FC<DraggableCardProps> = ({
  item,
  styles,
  onPress,
  onDragStart,
  onDragMove,
  onDragEnd,
  isDragging,
}) => {
  const didDrag = useRef(false)

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_, state) =>
        Math.abs(state.dx) > 4 || Math.abs(state.dy) > 4,
      onPanResponderGrant: (e) => {
        didDrag.current = false
        onDragStart(
          item,
          e.nativeEvent.pageX,
          e.nativeEvent.pageY,
          e.nativeEvent.locationX,
          e.nativeEvent.locationY,
        )
      },
      onPanResponderMove: (e) => {
        didDrag.current = true
        onDragMove(e.nativeEvent.pageX, e.nativeEvent.pageY)
      },
      onPanResponderRelease: (e) => {
        onDragEnd(e.nativeEvent.pageX, e.nativeEvent.pageY)
        if (!didDrag.current) onPress()
        didDrag.current = false
      },
      onPanResponderTerminate: (e) => {
        onDragEnd(e.nativeEvent.pageX, e.nativeEvent.pageY)
        didDrag.current = false
      },
    })
  ).current

  return (
    <View
      {...panResponder.panHandlers}
      style={[styles.card, isDragging && styles.cardDragging]}
    >
      <View style={styles.cardContent}>
        <Image
          source={item.contact.avatarUrl ? { uri: item.contact.avatarUrl } : defaultImage}
          style={styles.avatar}
        />
        <View style={styles.cardInfo}>
          <Text style={styles.contactName} numberOfLines={1} selectable={false}>
            {item.contact.firstName} {item.contact.lastName}
          </Text>
          {!!item.name && (
            <Text style={styles.reminderName} numberOfLines={1} selectable={false}>
              {item.name}
            </Text>
          )}
        </View>
      </View>
    </View>
  )
}

export const BoardView: React.FC<BoardViewProps> = ({ keepContacts, onEditReminder, t }) => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const { mutate: updateKeepContact } = useUpdateKeepContact()

  // Optimistic local copy — immediately reflects drops before API responds.
  // pendingIds tracks items with in-flight mutations so incoming refetches
  // don't overwrite our optimistic version until the server confirms.
  const [localContacts, setLocalContacts] = useState<KeepContact[]>(keepContacts)
  const pendingIds = useRef<Set<string>>(new Set())
  useEffect(() => {
    setLocalContacts(prev =>
      keepContacts.map(serverItem => {
        if (pendingIds.current.has(serverItem.id)) {
          // Keep our optimistic version for this item
          return prev.find(local => local.id === serverItem.id) ?? serverItem
        }
        return serverItem
      })
    )
  }, [keepContacts])

  const scrollX = useRef(0)
  const containerRef = useRef<View>(null)
  const containerOrigin = useRef({ x: 0, y: 0 })
  const draggingItemRef = useRef<KeepContact | null>(null)
  const dropTargetRef = useRef<string | null>(null)
  // Offset within the card where the user grabbed it
  const grabOffset = useRef({ x: 0, y: 0 })

  const [draggingItem, setDraggingItem] = useState<KeepContact | null>(null)
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 })
  const [dropTarget, setDropTarget] = useState<string | null>(null)

  const columns = useMemo((): FrequencyColumn[] => {
    const columnOrder = ['never', 'day', 'week', '2weeks', 'month', '6weeks', '3months', '6months', 'year', 'custom']
    const labels = getRecurrenceLabels(t)

    const grouped: Record<string, KeepContact[]> = {}
    for (const kc of localContacts) {
      const key = getFrequencyKey(kc)
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(kc)
    }

    return columnOrder.map(key => ({
      key,
      label: labels[key] || key,
      items: grouped[key] ?? [],
    }))
  }, [localContacts, t])

  const getColumnKeyAtX = useCallback((pageX: number): string | null => {
    const relX = pageX - containerOrigin.current.x + scrollX.current
    const idx = Math.floor(relX / COLUMN_TOTAL)
    if (idx < 0 || idx >= columns.length) return null
    return columns[idx].key
  }, [columns])

  const handleDragStart = useCallback((
    item: KeepContact,
    pageX: number,
    pageY: number,
    offsetX: number,
    offsetY: number,
  ) => {
    containerRef.current?.measure((_fx, _fy, _w, _h, px, py) => {
      containerOrigin.current = { x: px, y: py }
    })
    grabOffset.current = { x: offsetX, y: offsetY }
    draggingItemRef.current = item
    setDraggingItem(item)
    setDragPos({ x: pageX, y: pageY })
    const col = getColumnKeyAtX(pageX)
    dropTargetRef.current = col
    setDropTarget(col)
  }, [getColumnKeyAtX])

  const handleDragMove = useCallback((pageX: number, pageY: number) => {
    setDragPos({ x: pageX, y: pageY })
    const col = getColumnKeyAtX(pageX)
    if (col !== dropTargetRef.current) {
      dropTargetRef.current = col
      setDropTarget(col)
    }
  }, [getColumnKeyAtX])

  const handleDragEnd = useCallback((pageX: number, pageY: number) => {
    const item = draggingItemRef.current
    const targetKey = getColumnKeyAtX(pageX) ?? dropTargetRef.current
    const sourceKey = item ? getFrequencyKey(item) : null

    if (item && targetKey && targetKey !== sourceKey && targetKey !== 'custom') {
      const newRrule = buildRrule({
        type: targetKey as SimpleRecurrenceType,
        dtstart: new Date(item.keepDate),
      })
      // Optimistic: move card immediately, guard against incoming refetches
      pendingIds.current.add(item.id)
      setLocalContacts(prev => [
        { ...item, rrule: newRrule },
        ...prev.filter(kc => kc.id !== item.id),
      ])
      updateKeepContact({ id: item.id, rrule: newRrule }, {
        onSuccess: () => { pendingIds.current.delete(item.id) },
        onError: (error) => {
          // Revert to server state on failure
          pendingIds.current.delete(item.id)
          setLocalContacts(keepContacts)
          showApiErrorToast(t, error)
        },
      })
    }

    draggingItemRef.current = null
    dropTargetRef.current = null
    setDraggingItem(null)
    setDropTarget(null)
  }, [getColumnKeyAtX, updateKeepContact, keepContacts])

  return (
    <View ref={containerRef} style={{ flex: 1, ...(Platform.OS === 'web' ? { userSelect: 'none' } : {}) }}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.container}
        scrollEnabled={!draggingItem}
        onScroll={e => { scrollX.current = e.nativeEvent.contentOffset.x }}
        scrollEventThrottle={16}
      >
        {columns.map(column => (
          <View
            key={column.key}
            style={[
              styles.column,
              dropTarget === column.key &&
                draggingItem &&
                dropTarget !== getFrequencyKey(draggingItem) &&
                column.key !== 'custom' &&
                styles.columnDropTarget,
            ]}
          >
            <View style={styles.columnHeader}>
              <Text style={styles.columnTitle} selectable={false}>{column.label}</Text>
              <View style={styles.countBadge}>
                <Text style={styles.countText} selectable={false}>{column.items.length}</Text>
              </View>
            </View>
            <ScrollView style={styles.columnContent} showsVerticalScrollIndicator={false} scrollEnabled={!draggingItem}>
              {column.items.map(item => (
                <DraggableCard
                  key={item.id}
                  item={item}
                  styles={styles}
                  onPress={() => onEditReminder(item)}
                  onDragStart={handleDragStart}
                  onDragMove={handleDragMove}
                  onDragEnd={handleDragEnd}
                  isDragging={draggingItem?.id === item.id}
                />
              ))}
            </ScrollView>
          </View>
        ))}
      </ScrollView>

      {draggingItem && (
        <View
          pointerEvents="none"
          style={[
            styles.dragOverlay,
            {
              top: dragPos.y - containerOrigin.current.y - grabOffset.current.y,
              left: dragPos.x - containerOrigin.current.x - grabOffset.current.x,
            },
          ]}
        >
          <View style={styles.cardContent}>
            <Image
              source={draggingItem.contact.avatarUrl ? { uri: draggingItem.contact.avatarUrl } : defaultImage}
              style={styles.avatar}
            />
            <View style={styles.cardInfo}>
              <Text style={styles.contactName} selectable={false} numberOfLines={1}>
                {draggingItem.contact.firstName} {draggingItem.contact.lastName}
              </Text>
              {!!draggingItem.name && (
                <Text style={styles.reminderName} selectable={false} numberOfLines={1}>
                  {draggingItem.name}
                </Text>
              )}
            </View>
          </View>
        </View>
      )}
    </View>
  )
}

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: theme.spacing.sm,
  },
  column: {
    width: COLUMN_WIDTH,
    backgroundColor: theme.colors.grey + '30',
    borderRadius: 12,
    marginHorizontal: theme.spacing.sm,
    paddingBottom: theme.spacing.md,
    maxHeight: '100%',
  },
  columnDropTarget: {
    backgroundColor: theme.colors.primary + '20',
    borderWidth: 2,
    borderColor: theme.colors.primary,
    borderStyle: 'dashed',
  },
  columnHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.md,
  },
  columnTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.secondary,
  },
  countBadge: {
    backgroundColor: theme.colors.primary + '20',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  countText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.primary,
  },
  columnContent: {
    paddingHorizontal: theme.spacing.sm,
  },
  card: {
    cursor: 'pointer',
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.sm,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 2px rgba(0,0,0,0.08)', userSelect: 'none' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.05,
          shadowRadius: 2,
          elevation: 1,
        }),
  },
  cardDragging: {
    opacity: 0.3,
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: theme.spacing.sm,
  },
  cardInfo: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text,
  },
  reminderName: {
    fontSize: 12,
    color: theme.colors.greyBlue,
    marginTop: 2,
  },
  dragOverlay: {
    position: 'absolute',
    width: COLUMN_WIDTH,
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    padding: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 24px rgba(0,0,0,0.18)', userSelect: 'none' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.18,
          shadowRadius: 12,
          elevation: 12,
        }),
    zIndex: 1000,
    opacity: 0.95,
  },
})
